import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { enqueueSnackbar } from 'notistack';
import { ReactNode, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { AppFileRouterEndpoint } from 'src/interfaces/AppFileRouter';
import { MAX_FILES_PER_UPLOAD } from 'src/common/constants/uploadLimits';

import {
  getMergedAcceptConfig,
  groupFilesByEndpoint,
  resolveEndpointForFile,
  UPLOAD_ENDPOINTS,
} from './resolveUploadEndpoint';
import { validateUploadBatchSize } from './uploadBatchUtils';
import { useFileUploadBatch } from './useFileUploadBatch';

type FileListDropOverlayProps = {
  children: ReactNode;
  enabled?: boolean;
  lockedEndpoint?: AppFileRouterEndpoint;
  activeEndpoint: 'all' | AppFileRouterEndpoint;
};

const FileListDropOverlay = ({
  children,
  enabled = true,
  lockedEndpoint,
  activeEndpoint,
}: FileListDropOverlayProps) => {
  const { t } = useTranslation();
  const { isUploading, uploadFiles, uploadFilesGrouped } = useFileUploadBatch();

  const effectiveEndpoint = lockedEndpoint ?? (activeEndpoint !== 'all' ? activeEndpoint : null);
  const allowedEndpoints = lockedEndpoint ? [lockedEndpoint] : UPLOAD_ENDPOINTS;
  const acceptConfig = effectiveEndpoint
    ? getMergedAcceptConfig([effectiveEndpoint])
    : getMergedAcceptConfig(allowedEndpoints);

  const overlayHintEndpoint = effectiveEndpoint ?? 'image';

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const validation = validateUploadBatchSize(acceptedFiles.length);
      if (!validation.valid) {
        enqueueSnackbar(t('form.upload.tooManyFiles', { max: validation.max }), { variant: 'error' });
        return;
      }

      if (effectiveEndpoint) {
        await uploadFiles(acceptedFiles, effectiveEndpoint);
        return;
      }

      const groups = groupFilesByEndpoint(acceptedFiles);
      const unsupported = acceptedFiles.filter(file => !resolveEndpointForFile(file));

      if (unsupported.length > 0) {
        enqueueSnackbar(t('form.upload.error'), { variant: 'error' });
      }

      if (groups.size === 0) {
        return;
      }

      await uploadFilesGrouped(groups);
    },
    [effectiveEndpoint, t, uploadFiles, uploadFilesGrouped],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptConfig,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !enabled || isUploading,
  });

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <Box
      {...getRootProps()}
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <input {...getInputProps()} />
      {children}
      {(isDragActive || isUploading) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            bgcolor: 'action.hover',
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
            pointerEvents: isUploading ? 'none' : 'auto',
          }}
        >
          {isUploading ? (
            <>
              <CircularProgress size={48} />
              <Typography variant="h6">{t('form.upload.uploadingFiles')}</Typography>
            </>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="h6">{t('system.fileManager.dropToUpload')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`form.upload.${overlayHintEndpoint}.supportedFormats`)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('form.upload.maxFilesPerBatch', { max: MAX_FILES_PER_UPLOAD })}
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FileListDropOverlay;
